import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeftIcon, ArrowPathIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { TOKEN_SYMBOL, formatTokenAmount } from '../config/token';
import RepairFeedbackBanner from './RepairFeedbackBanner';
import { walletService } from '../services/walletService';

interface Transaction {
  id: string;
  transactionId: string;
  amount: number;
  type: string;
  status: string;
  signature: string | null;
  metadata: any;
  fromWalletId: string;
  toWalletId: string;
  createdAt: string;
  updatedAt: string;
  errorMessage?: string | null;
}

const TransactionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairFeedback, setRepairFeedback] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (id) {
      fetchTransaction();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTransaction = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      const response = await axios.get(`${API_BASE_URL}/transaction/${id}`);
      setTransaction(response.data);
    } catch (error) {
      console.error('Error fetching transaction:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    if (!transaction) return;

    try {
      setRetrying(true);
      setActionError('');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      await axios.post(`${API_BASE_URL}/transaction/${transaction.transactionId}/retry`);
      fetchTransaction();
    } catch (error) {
      console.error('Error retrying transaction:', error);
      setActionError('Failed to retry transaction');
    } finally {
      setRetrying(false);
    }
  };

  const handleRepairWallets = async () => {
    if (!transaction) return;

    try {
      setRepairing(true);
      setActionError('');
      setRepairFeedback(null);
      const results = await walletService.repairTransactionWallets(
        transaction.fromWalletId,
        transaction.toWalletId
      );
      setRepairFeedback(walletService.summarizeRepairs(results));
    } catch (error: any) {
      setActionError(error.response?.data?.message || 'Failed to repair wallets');
    } finally {
      setRepairing(false);
    }
  };

  const handleRepairAndRetry = async () => {
    if (!transaction) return;

    try {
      setRepairing(true);
      setRetrying(true);
      setActionError('');
      setRepairFeedback(null);
      const results = await walletService.repairTransactionWallets(
        transaction.fromWalletId,
        transaction.toWalletId
      );
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      await axios.post(`${API_BASE_URL}/transaction/${transaction.transactionId}/retry`);
      await fetchTransaction();
      setRepairFeedback(`${walletService.summarizeRepairs(results)} Transaction retry submitted.`);
    } catch (error: any) {
      setActionError(error.response?.data?.message || 'Failed to repair and retry transaction');
    } finally {
      setRepairing(false);
      setRetrying(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-500/15 text-green-300 ring-1 ring-inset ring-green-500/30';
      case 'pending':
        return 'bg-yellow-500/15 text-yellow-300 ring-1 ring-inset ring-yellow-500/30';
      case 'failed':
        return 'bg-red-500/15 text-red-300 ring-1 ring-inset ring-red-500/30';
      default:
        return 'bg-slate-700 text-slate-300 ring-1 ring-inset ring-slate-600';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'send_to_customer':
        return 'bg-blue-500/15 text-blue-300 ring-1 ring-inset ring-blue-500/30';
      case 'transfer_to_merchant':
        return 'bg-purple-500/15 text-purple-300 ring-1 ring-inset ring-purple-500/30';
      case 'withdrawal':
        return 'bg-orange-500/15 text-orange-300 ring-1 ring-inset ring-orange-500/30';
      default:
        return 'bg-slate-700 text-slate-300 ring-1 ring-inset ring-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Transaction not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/transactions"
            className="inline-flex items-center px-3 py-2 border border-slate-600 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Transactions
          </Link>
          <h1 className="text-2xl font-semibold text-slate-100">Transaction Details</h1>
        </div>
        {transaction.status === 'failed' && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleRepairAndRetry}
              disabled={repairing || retrying}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500 disabled:opacity-50"
            >
              <WrenchScrewdriverIcon className={`h-4 w-4 mr-2 ${repairing ? 'animate-spin' : ''}`} />
              {repairing || retrying ? 'Working...' : 'Repair & Retry'}
            </button>
            <button
              onClick={handleRepairWallets}
              disabled={repairing}
              className="inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 disabled:opacity-50"
            >
              Repair wallets
            </button>
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${retrying ? 'animate-spin' : ''}`} />
              Retry only
            </button>
          </div>
        )}
      </div>

      {actionError && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {actionError}
        </div>
      )}

      {repairFeedback && (
        <RepairFeedbackBanner message={repairFeedback} onDismiss={() => setRepairFeedback(null)} />
      )}

      <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-slate-100">
            Transaction Information
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">
            Complete details of the transaction
          </p>
        </div>
        <div className="border-t border-slate-700">
          <dl>
            <div className="bg-slate-800/40 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-slate-400">Transaction ID</dt>
              <dd className="mt-1 text-sm text-slate-100 sm:mt-0 sm:col-span-2 font-mono">
                {transaction.transactionId}
              </dd>
            </div>
            <div className="bg-slate-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-slate-400">Amount</dt>
              <dd className="mt-1 text-sm text-slate-100 sm:mt-0 sm:col-span-2">
                {formatTokenAmount(transaction.amount)} {TOKEN_SYMBOL}
              </dd>
            </div>
            <div className="bg-slate-800/40 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-slate-400">Type</dt>
              <dd className="mt-1 text-sm text-slate-100 sm:mt-0 sm:col-span-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(transaction.type)}`}>
                  {transaction.type.replace(/_/g, ' ')}
                </span>
              </dd>
            </div>
            <div className="bg-slate-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-slate-400">Status</dt>
              <dd className="mt-1 text-sm text-slate-100 sm:mt-0 sm:col-span-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                  {transaction.status}
                </span>
                {transaction.errorMessage && (
                  <p className="mt-2 text-sm text-red-400">{transaction.errorMessage}</p>
                )}
              </dd>
            </div>
            {transaction.signature && (
              <div className="bg-slate-800/40 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-slate-400">Blockchain Signature</dt>
                <dd className="mt-1 text-sm text-slate-100 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center space-x-2">
                    <code className="font-mono text-xs bg-slate-900 text-slate-200 px-2 py-1 rounded break-all">
                      {transaction.signature}
                    </code>
                    <button
                      onClick={() => navigator.clipboard.writeText(transaction.signature || '')}
                      className="inline-flex items-center px-2 py-1 text-xs text-indigo-400 hover:text-indigo-300 border border-indigo-500/40 rounded"
                      title="Copy signature"
                    >
                      📋 Copy
                    </button>
                    <a
                      href={`https://explorer.solana.com/tx/${transaction.signature}?cluster=devnet`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-2 py-1 text-xs text-blue-400 hover:text-blue-300 border border-blue-500/40 rounded"
                      title="View on Solana Explorer"
                    >
                      🔗 Explorer
                    </a>
                  </div>
                </dd>
              </div>
            )}
            <div className="bg-slate-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-slate-400">From Wallet ID</dt>
              <dd className="mt-1 text-sm text-slate-100 sm:mt-0 sm:col-span-2 font-mono">
                {transaction.fromWalletId}
              </dd>
            </div>
            <div className="bg-slate-800/40 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-slate-400">To Wallet ID</dt>
              <dd className="mt-1 text-sm text-slate-100 sm:mt-0 sm:col-span-2 font-mono">
                {transaction.toWalletId}
              </dd>
            </div>
            <div className="bg-slate-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-slate-400">Created At</dt>
              <dd className="mt-1 text-sm text-slate-100 sm:mt-0 sm:col-span-2">
                {new Date(transaction.createdAt).toLocaleString()}
              </dd>
            </div>
            <div className="bg-slate-800/40 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-slate-400">Updated At</dt>
              <dd className="mt-1 text-sm text-slate-100 sm:mt-0 sm:col-span-2">
                {new Date(transaction.updatedAt).toLocaleString()}
              </dd>
            </div>
            {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
              <div className="bg-slate-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-slate-400">Metadata</dt>
                <dd className="mt-1 text-sm text-slate-100 sm:mt-0 sm:col-span-2">
                  <pre className="bg-slate-900 text-slate-200 p-3 rounded-md overflow-x-auto text-xs">
                    {JSON.stringify(transaction.metadata, null, 2)}
                  </pre>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetail;
